import React, { useRef, useEffect, useState } from 'react';
import { useIntl } from '../../util/reactIntl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import AliceCarousel from 'react-alice-carousel';
import { FormattedMessage } from '../../util/reactIntl';
import { ListingCard, IconSpinner, IconCarouselArrows, NamedLink, IconSearch } from '../../components';
import { fetchListings } from '../../ducks/listings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import css from './ListingsCarousel.module.css';
import { useConfiguration } from '../../context/configurationContext';

const MODAL_BREAKPOINT = 768;

// Sharetribe `ids` query ignores input order (default sort: createdAt).
const sortListingsByIds = (listings = [], ids = []) => {
  const byUuid = listings.reduce((acc, listing) => {
    acc[listing.id.uuid] = listing;
    return acc;
  }, {});
  return ids.map(id => byUuid[id]).filter(Boolean);
};

const ListingsCarouselComponent = props => {

  const {
    type,
    onFetchListings,
    homepageListings,
    homepageListingsInProgress,

    landscapeListings,
    landscapeListingsInProgress,

    listingsById,
    listingsByIdInProgress,

    pageId,
    listingIds,
    callToAction,

    theme = "light",
    autoPlay = true,
    setCarouselListings,
    setExperienceRewildingBg
  } = props;

  const config = useConfiguration();
  const intl = useIntl();

  const isDarkTheme = theme === "dark";
  const isLightTheme = theme === "light";

  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;

  const [realIndex, setRealIndex] = useState(0);
  const [internalIndex, setInternalIndex] = useState(0);
  const carouselRef = useRef(null);

  let queryParams = {};
  let listings = [];
  let inProgress = null;

  switch (type) {
    case 'homepageListings':
      queryParams = {
        pub_showOnHomepage: true,
        perPage: 10,
      };
      listings = homepageListings;
      inProgress = homepageListingsInProgress;
      break;
    case 'landscapeListings':
      queryParams = {
        pub_field_landscape: pageId?.replace("landscape-", ""),
        perPage: 10,
      };
      listings = landscapeListings;
      inProgress = landscapeListingsInProgress;
      break;
    case 'listingsById':
      queryParams = {
        ids: (listingIds || []).join(','),
        perPage: listingIds?.length || 10,
      };
      listings = sortListingsByIds(listingsById, listingIds);
      inProgress = listingsByIdInProgress;
      break;

    default:
      queryParams = {}
      break;
  }

  useEffect(() => {
    if (type === 'listingsById' && !(listingIds || []).length) {
      return;
    }
    onFetchListings(type, queryParams, config)
  }, []);

  useEffect(() => {
    setCarouselListings?.(listings)
  }, [listings, inProgress, type]);

  const cardRenderSizes = () => {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  };

  const carouselItems = listings?.map(l => (

    <div className={css.listingCardHolder} key={l.id.uuid}>
      <ListingCard
        className={css.listingCard}
        key={l.id.uuid}
        listing={l}
        renderSizes={cardRenderSizes()}
        setExperienceRewildingBg={setExperienceRewildingBg}
      />
    </div>
  ));

  const prevButton = () => (
    <button className={css.prevButton} type="button">
      <IconCarouselArrows type="white-left" />
    </button>
  );

  const nextButton = () => (
    <button className={css.nextButton} type="button">
      <IconCarouselArrows type="white-right" />
    </button>
  );

  if (inProgress) {
    return (
      <div className={css.root}>
        <div className={css.listingsSpinnerHolder}>
          <IconSpinner />
        </div>
      </div>
    )
  }

  const itemsCount = carouselItems.length;
  const showFullCarousel = itemsCount > 4;
  const itemsToShow = 4;

  const goTo = (targetReal) => {
    if (!carouselRef.current) return;
    const currentInternal = internalIndex;
    const currentReal = realIndex;
    const deltaForward = (targetReal - currentReal + itemsCount) % itemsCount;
    const deltaBackward = (currentReal - targetReal + itemsCount) % itemsCount;
    const moveBy = deltaForward <= deltaBackward ? deltaForward : -deltaBackward;
    const newInternal = currentInternal + moveBy;

    carouselRef.current?.slideTo(newInternal);
  };

  if (!carouselItems?.length) return null;

  return (
    <div className={classNames(css.root)}>
      <div className={css.listingsCarouselHolder}>
        <AliceCarousel
          className={css.listingsCarousel}
          ref={carouselRef}
          slideBy={1}
          disableDotsControls
          infinite={showFullCarousel}
          items={carouselItems}
          autoHeight={!isMobileLayout}
          autoPlay={autoPlay}
          autoPlayInterval={2000}
          responsive={{
            0: { items: 1 },
            860: { items: Math.min(itemsCount, 2), itemsFit: 'contain' },
            1024: {
              items: showFullCarousel ? 2 : itemsToShow,
              itemsFit: 'contain',
            },
            1280: {
              items: showFullCarousel ? 4 : itemsToShow,
              itemsFit: 'contain',
            },
            1921: {
              items: showFullCarousel ? 5 : itemsToShow,
              itemsFit: 'contain',
            },
          }}
          renderPrevButton={showFullCarousel || isMobileLayout ? prevButton : () => null}
          renderNextButton={showFullCarousel || isMobileLayout ? nextButton : () => null}
          onSlideChanged={({ item }) => {
            setInternalIndex(item);
            setRealIndex(((item % itemsCount) + itemsCount) % itemsCount);
          }}
        />
        {(showFullCarousel || isMobileLayout) && (
          <div className={classNames(css.customDots, isDarkTheme && css.customDotsDark)}>
            {carouselItems.map((_, i) => (
              <button
                key={i}
                className={classNames(css.dot, { [css.dotActive]: i === realIndex })}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        )}
      </div>

      {!!callToAction?.content && !!callToAction?.href && (
        <div className={css.showAllListingsHolder}>
          <a
            className={classNames(
              isLightTheme && css.showAllListings,
              isDarkTheme && css.showAllListingsDark
            )}
            href={callToAction.href}>
            <IconSearch />
            {callToAction.content}
          </a>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  const homepageListingsIds = state?.listings?.homepageListings?.resultIds;
  const homepageListings = !!homepageListingsIds ? getListingsById(state, homepageListingsIds) : [];
  const homepageListingsInProgress = state?.listings?.homepageListings?.inProgress;

  const landscapeListingsIds = state?.listings?.landscapeListings?.resultIds;
  const landscapeListings = !!landscapeListingsIds ? getListingsById(state, landscapeListingsIds) : [];
  const landscapeListingsInProgress = state?.listings?.landscapeListings?.inProgress;

  const listingsByIdIds = state?.listings?.listingsById?.resultIds;
  const listingsById = !!listingsByIdIds ? getListingsById(state, listingsByIdIds) : [];
  const listingsByIdInProgress = state?.listings?.listingsById?.inProgress;

  return {
    currentUser,

    homepageListings,
    homepageListingsInProgress,

    landscapeListings,
    landscapeListingsInProgress,

    listingsById,
    listingsByIdInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onFetchListings: (type, queryParams, config) =>
    dispatch(fetchListings(type, queryParams, config)),
});

const ListingsCarousel = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ListingsCarouselComponent);

export default ListingsCarousel;


